import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { useRollbar } from '@rollbar/react';
import isEqual from 'lodash/isEqual';

import useAuthentication from '../hooks/useAuthentication';
import useUserManagement from '../hooks/useUserManagement';
import useProjectList from '../hooks/useProjectList';
import useBrowserTabTitle from '../hooks/useBrowserTabTitle'

const ANALYTICS_STORAGE_KEY = 'analytics_data';

const Main = () => {
    const rollbar = useRollbar();
    const { currentUser } = useAuthentication();
    useBrowserTabTitle('Home')

    const {
        userProjectIsFavourite,
        userAddFavouriteProject,
        userRemoveFavouriteProject,
        userHasRole,
        userHasExplicitProjectAccess,
        userCanCreateOpportunity,
        userCanCreateProject,
        userCanCreateProduct,
    } = useUserManagement()

    const {
        projects,
    } = useProjectList()

    const [recentProjects, setRecentProjects] = useState([])

    const [favouriteProjects, setFavouriteProjects] = useState([])
    const [otherProjects, setOtherProjects] = useState([])

    useEffect(() => {
        const analyticsData = {
            os: navigator.platform,
            userAgent: navigator.userAgent,
            language: navigator.language,
            languages: navigator.languages ? [...navigator.languages] : [],
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            screenColorDepth: window.screen.colorDepth,
            devicePixelRatio: window.devicePixelRatio,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            cookiesEnabled: navigator.cookieEnabled,
            touchSupport: navigator.maxTouchPoints > 0,
            hardwareConcurrency: navigator.hardwareConcurrency,
            deviceMemory: navigator.deviceMemory,
        };

        try {
            const previousData = localStorage.getItem(ANALYTICS_STORAGE_KEY);
            if (previousData && isEqual(JSON.stringify(analyticsData), previousData)) {
                return;
            }
        } catch (e) {
            // Corrupted localStorage data, proceed with POST
        }

        fetch('/v0/analytics', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(analyticsData),
        })
            .then(response => {
                if (!response.ok) throw response;
                localStorage.setItem(ANALYTICS_STORAGE_KEY, JSON.stringify(analyticsData));
            })
            .catch(error => {
                if (error?.status === 403) return;
                const extra = {
                    currentUserId: currentUser?.id,
                    url: error?.url,
                    status: error?.status,
                    statusText: error?.statusText,
                }
                if (error?.text) {
                    error.text().then(body => {
                        rollbar?.error?.('Main analytics POST error', error, { ...extra, body });
                    }).catch(() => {
                        rollbar?.error?.('Main analytics POST error', error, extra);
                    })
                } else {
                    rollbar?.error?.('Main analytics POST error', error, extra);
                }
            });
    }, [])

    useEffect(() => {
        fetch('/v0/account/recent_projects', { credentials: 'same-origin' })
            .then(function(response) {
                if (!response.ok) throw response
                return response.json()
            })
            .then((json) => {
                const recentProjectsMapped = json?.results
                    ?.sort((a,b) => b.id < a.id ? 1 : 0)
                    ?.map(item => item.project_id) ?? []

                setRecentProjects(recentProjectsMapped);
            })
            .catch(error => {
                if (error?.status === 403) return;
                const extra = {
                    currentUserId: currentUser?.id,
                    url: error?.url,
                    status: error?.status,
                    statusText: error?.statusText,
                }
                if (error?.text) {
                    error.text().then(body => {
                        rollbar?.error?.('Main getRecentProjects error', error, { ...extra, body })
                    }).catch(() => {
                        rollbar?.error?.('Main getRecentProjects error', error, extra)
                    })
                } else {
                    rollbar?.error?.('Main getRecentProjects error', error, extra)
                }
            })
    }, [])
    
    useEffect(() => {
        const maxFavouritesLength = 50;
        const maxOtherLength = 5;
        // If we're only showing recent projects, we should probably change the label
        const recentProjectsMapped = projects
            .filter(({ id }) => recentProjects.includes(id))
            .filter(({ id }) => userHasExplicitProjectAccess(currentUser, id));

        const favourites = recentProjectsMapped
            .filter(({ id }) => userProjectIsFavourite(currentUser, id))
            .slice(0, maxFavouritesLength);
        
        const others = recentProjectsMapped
            .filter(({ id }) => !userProjectIsFavourite(currentUser, id))
            .slice(0, maxOtherLength);

        setFavouriteProjects(favourites);
        setOtherProjects(others);
    }, [recentProjects, projects, currentUser])

    const addFavouriteProject = (event, projectID) => {
        event.preventDefault()
        userAddFavouriteProject(currentUser,projectID)
    }
    
    const removeFavouriteProject = (event, projectID) => {
        event.preventDefault()
        userRemoveFavouriteProject(currentUser,projectID)
    }
    
    const renderProjectList = () => {
        if( favouriteProjects.length === 0 && otherProjects.length === 0 ) {
            return (
                <div className="ibox ">
                    <div className="ibox-title">
                        <h5>Favourites</h5>
                    </div>
                    <div className="ibox-content" style={{color: 'rgba(103, 106, 108, 0.65)'}}>
                        Your favourites will be listed below.
                    </div>
                </div>
            )
        }

        return (
            <div className="ibox">
                <div className="ibox-title">
                    <h5>Favourites</h5>
                </div>
                <div className="ibox-content table-responsive">
                    <table className="table table-hover no-margins">
                        <thead>
                            <tr>
                                <th>Code</th>
                                <th>Name</th>
                                <th>&nbsp;</th>
                            </tr>
                        </thead>
                        <tbody>
                            {favouriteProjects.map(project => (
                                 <tr key={`main-${project.id}`}>
                                     <td><Link to={`/portfolio/${project.id}/dashboard`}>{project.project_code}</Link></td>
                                     <td><Link to={`/portfolio/${project.id}/dashboard`}>{project.name}</Link></td>
                                     <td>
                                         <a href="#" onClick={(event) => removeFavouriteProject(event, project.id)}
                                            className="set-favourite-false"
                                            data-tooltip-id="gnctooltip" data-tooltip-content="Unpin from Favourites">
                                             <i className="fa fa-thumb-tack"></i>
                                         </a>
                                     </td>
                                 </tr>
                             ))}
                            {otherProjects.map(project => (
                                 <tr key={`main-${project.id}`}>
                                     <td><Link to={`/portfolio/${project.id}/dashboard`}>{project.project_code}</Link></td>
                                     <td><Link to={`/portfolio/${project.id}/dashboard`}>{project.name}</Link></td>
                                     <td>
                                         <a href="#" onClick={(event) => addFavouriteProject(event, project.id)}
                                            className="set-favourite-true"
                                            data-tooltip-id="gnctooltip" data-tooltip-content="Pin to Favourites">
                                             <i className="fa fa-thumb-tack"></i>
                                         </a>
                                     </td>
                                 </tr>
                             ))}
                        </tbody>
                    </table>
                </div>
            </div>
        )
    }

    const renderAdmin = () => {
        let admin = [];

        if( userHasRole(currentUser,'user_admin') ) {
            admin.push(
                <li key="admin-1">
                    <Link to="/users"><i className="fa fa-user"></i>&nbsp; <span>Users</span></Link>
                    <Link to="/users/new" className="pull-right" data-tooltip-id="gnctooltip" data-tooltip-content="New User">
                        <i className="fa fa-plus-circle"></i></Link>
                </li>
            )
            admin.push(
                <li key="admin-2">
                    <Link to="/usergroups"><i className="fa fa-users"></i>&nbsp; <span>User Groups</span></Link>
                    <Link to="/usergroups/new" className="pull-right" data-tooltip-id="gnctooltip" data-tooltip-content="New User Group">
                        <i className="fa fa-plus-circle"></i></Link>
                </li>
            )
            admin.push(
                <li key="admin-3">
                    <Link to="/security"><i className="fa fa-shield"></i>&nbsp; <span>Security</span></Link>
                </li>
            )

        }

        if( userCanCreateOpportunity(currentUser) ) {
            admin.push(
                <li key="admin-4">
                    <Link to="/portfolio?filter=ALL_OPPORTUNITIES"><i className="fa fa-handshake-o"></i>&nbsp; <span>Opportunities</span></Link>
                    <Link to="/portfolio/new/information?project_type_id=2" className="pull-right" data-tooltip-id="gnctooltip" data-tooltip-content="New Opportunity">
                        <i className="fa fa-plus-circle"></i></Link>
                </li>
            )
        }

        if( userCanCreateProject(currentUser) ) {
            admin.push(
                <li key="admin-5">
                    <Link to="/portfolio?filter=ALL_PROJECTS"><i className="fa fa-space-shuttle"></i>&nbsp; <span>Projects</span></Link>
                    <Link to="/portfolio/new/information?project_type_id=1" className="pull-right" data-tooltip-id="gnctooltip" data-tooltip-content="New Project">
                        <i className="fa fa-plus-circle"></i></Link>
                </li>
            )
        }

        if( userCanCreateProduct(currentUser) ) {
            admin.push(
                <li key="admin-6">
                    <Link to="/portfolio?filter=ALL_PRODUCTS"><i className="fa fa-cube"></i>&nbsp; <span>Products</span></Link>
                    <Link to="/portfolio/new/information?project_type_id=3" className="pull-right" data-tooltip-id="gnctooltip" data-tooltip-content="New Product">
                        <i className="fa fa-plus-circle"></i></Link>
                </li>
            )
        }

        if( userHasRole(currentUser, 'cadm_admin') ) {
            admin.push(
                <li key="admin-7">
                    <Link to={'/cadm'}><i className="fa fa-wrench"></i>&nbsp; <span>CADM</span></Link>
                </li>
            )
        }

        if( admin.length === 0 ) {
            return <div />
        }

        return (
            <div className="ibox ">
                <div className="ibox-title">
                    <h5>Admin</h5>
                </div>
                <div className="ibox-content">
                    <ul className="todo-list m-t small-list">
                        {admin}
                    </ul>
                </div>
            </div>
        )
    }

    const renderQuality = () => {
        let quality = []
        if( userHasRole(currentUser, 'serial_numbers_readonly') ) {
            const newLink = userHasRole(currentUser, 'serial_numbers_user') ? 
                (<Link to="/serial_numbers/new" className="pull-right" data-tooltip-id="gnctooltip" data-tooltip-content="New Serial Number">
                    <i className="fa fa-plus-circle"></i></Link>) :
                null
            quality.push(
                <li key="quality-1">
                    <Link to={'/serial_numbers'}><i className="fa fa-list-ul"></i>&nbsp; <span>Serial Numbers</span></Link>
                    {newLink}
                </li>
            )
        }
        if( userHasRole(currentUser, 'qms_documents_readonly') ) {
            const newLink = userHasRole(currentUser, 'qms_documents_user') ? 
                (<Link to="/qms_documents/new" className="pull-right" data-tooltip-id="gnctooltip" data-tooltip-content="New QMS Document">
                    <i className="fa fa-plus-circle"></i></Link>) :
                null
            quality.push(
                <li key="quality-2">
                    <Link to={'/qms_documents'}><i className="fa fa-file-word-o"></i>&nbsp; <span>QMS Documents</span></Link>
                    {newLink}
                </li>
            )
        }
        if( userHasRole(currentUser, 'qa_documents_readonly') ) {
            const newLink = userHasRole(currentUser, 'qa_documents_user') ? 
                (<Link to="/qa_documents/new" className="pull-right" data-tooltip-id="gnctooltip" data-tooltip-content="New QA Document">
                    <i className="fa fa-plus-circle"></i></Link>) :
                null
            quality.push(
                <li key="quality-3">
                    <Link to={'/qa_documents'}><i className="fa fa-file-word-o"></i>&nbsp; <span>QA Documents</span></Link>
                    {newLink}
                </li>
            )
        }
        if( userHasRole(currentUser, 'qa_records_readonly') ) {
            const newLink = userHasRole(currentUser, 'qa_records_user') ? 
                (<Link to="/qa_records/new" className="pull-right" data-tooltip-id="gnctooltip" data-tooltip-content="New QA Record">
                    <i className="fa fa-plus-circle"></i></Link>) :
                null

            quality.push(
                <li key="quality-4">
                    <Link to={'/qa_records'}><i className="fa fa-sticky-note-o"></i>&nbsp; <span>QA Records</span></Link>
                    {newLink}
                </li>
            )
        }

        if( quality.length === 0 ) {
            return <div />
        }

        return (
            <div className="ibox ">
                <div className="ibox-title">
                    <h5>Quality</h5>
                </div>
                <div className="ibox-content">
                    <ul className="todo-list m-t small-list">
                        {quality}
                    </ul>
                </div>
            </div>
        )
    }

    return (
        <div className="wrapper wrapper-content row">
            
            <div className="col-lg-8">
                <div className="row">

                    <div className="col-lg-6 stonly-main-favourites-box">
                        {renderProjectList()}
                    </div>

                    <div className="col-lg-6">
                        {renderQuality()}
                        {renderAdmin()}
                    </div>
                    
                </div>
            </div>

            <div className="col-lg-4 stonly-main-updates-box">
                <div className="ibox">
                    <div className="ibox-title">
                        <h5>GNC Go - Updates</h5>
                    </div>
                    <div className="ibox-content ibox-heading">
                        <h3><i className="fa fa-bullhorn"></i> What's New?</h3>
                        <small><i className="fa fa-tim"></i>GNC Go is built on the needs and feedback of the space community. Here's what's new in this release.</small>
                    </div>
                    <div className="ibox-content">
                        <div className="feed-activity-list">
                            <div className="feed-element">
                                <ul>
                                    <li><strong>SharePoint integration for Quality!</strong></li>
                                    <li>SMART templates save straight to SharePoint</li>
                                    <li>Autopopulated location links</li>
                                    <li>Auto-filing location controls</li>
                                    <li><strong>Plus!</strong> More API operations</li>
                                    <li>Tweaks, bug-fixes and more!</li>
                                    <li>
                                        <Link to="/changelog">
                                            View the changelog for details...
                                        </Link>
                                    </li>
                                </ul>
                            </div>
                            
                            <small className="text-muted">Version 83 beta (2026.10.11)</small>
                        </div>
                    </div>
                </div> 
            </div>

        </div>
    )
};

export default Main
